import { createContext, useContext, useState, useEffect } from 'react';
import { syncService } from '../offline_items/syncService';
import { dataFetchTracker } from './DataFetchTracker';

// ##### CONTEXT CREATION
const OfflineStatusContext = createContext();

// ##### OFFLINE STATUS PROVIDER COMPONENT
export const OfflineStatusProvider = ({ children }) => {
  const [status, setStatus] = useState({
    isOnline: navigator.onLine,
    isSyncing: false,
    isFetchingData: false,
    pendingCount: 0,
    retryingCount: 0,
    failedCount: 0,
    conflictCount: 0,
    currentItem: 0,
    totalItems: 0,
    successCount: 0,
    errorCount: 0,
    conflictDetected: false,
    conflictItemId: null,
    conflictItemName: null,
    conflictUrl: null
  });

  // ##### STATUS MONITORING EFFECT
  useEffect(() => {
    const updateStatus = async () => {
      const syncStatus = await syncService.getStatus();
      setStatus(prev => ({
        ...prev,
        ...syncStatus
      }));
    };

    updateStatus();

    // Listen to sync service (writes)
    const unsubscribeSync = syncService.addListener((syncUpdate) => {
      setStatus(prev => ({
        ...prev,
        ...syncUpdate
      }));
    });

    // Listen to data fetch tracker (reads)
    const unsubscribeFetch = dataFetchTracker.addListener((fetchUpdate) => {
      setStatus(prev => ({
        ...prev,
        ...fetchUpdate
      }));
    });

    const handleOnline = () => {
      setStatus(prev => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      setStatus(prev => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      unsubscribeSync();
      unsubscribeFetch();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <OfflineStatusContext.Provider value={status}>
      {children}
    </OfflineStatusContext.Provider>
  );
};

// ##### CUSTOM HOOK
export const useOfflineStatus = () => {
  const context = useContext(OfflineStatusContext);
  if (!context) {
    throw new Error('useOfflineStatus must be used within OfflineStatusProvider');
  }
  return context;
};
